import logo from '../../assets/teamly.png';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center">
          <img src={logo} alt="Teamly Logo" className="h-10 w-auto object-contain" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
          <p className="text-slate-500 text-sm mb-8">Last updated: March 18, 2026</p>

          <div className="space-y-10 text-slate-700 leading-relaxed">

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3">1. Introduction</h2>
              <p>
                Teamly ("we", "our", or "us") is a Human Resource Management System (HRMS) platform developed and operated by Spatium Offices. We are committed to protecting the privacy and security of personal data processed through our platform.
              </p>
              <p className="mt-3">
                This Privacy Policy explains how we collect, use, store, and disclose personal information when organizations ("Clients") and their employees ("Users") use our services. By using Teamly, you agree to the practices described in this policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3">2. Information We Collect</h2>
              <p className="mb-3">We collect the following categories of personal information:</p>

              <h3 className="font-semibold text-slate-800 mb-2">2.1 Employee Information</h3>
              <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
                <li>Full name, email address, phone number</li>
                <li>Date of birth, gender, address</li>
                <li>Employee code, designation, department, work location</li>
                <li>Date of joining, employment status</li>
                <li>Profile photograph (optional)</li>
                <li>Emergency contact details</li>
              </ul>

              <h3 className="font-semibold text-slate-800 mb-2">2.2 Financial Information</h3>
              <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
                <li>Bank account details (account number, IFSC code, bank name, branch)</li>
                <li>PAN card number</li>
                <li>Aadhaar number</li>
                <li>Provident Fund (PF) account number and UAN</li>
                <li>Salary structure and payroll information</li>
                <li>Tax declarations and investment proofs</li>
              </ul>

              <h3 className="font-semibold text-slate-800 mb-2">2.3 Attendance & Leave Data</h3>
              <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
                <li>Check-in and check-out times</li>
                <li>Leave requests, approvals, and balances</li>
                <li>Attendance records and working hours</li>
              </ul>

              <h3 className="font-semibold text-slate-800 mb-2">2.4 Account & Usage Information</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Login credentials (passwords are stored as cryptographic hashes and never in plain text)</li>
                <li>IP address and browser/device information for session management</li>
                <li>Activity logs for audit and security purposes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3">3. How We Use Your Information</h2>
              <p className="mb-3">We use the information collected for the following purposes:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>To provide and operate the HRMS services (payroll processing, leave management, attendance tracking, etc.)</li>
                <li>To generate payslips, tax documents (Form 16), appointment letters, and other employment documents</li>
                <li>To manage employee onboarding and offboarding workflows</li>
                <li>To facilitate performance reviews and goal tracking</li>
                <li>To send transactional notifications (payslip alerts, leave approvals, onboarding links)</li>
                <li>To maintain security, prevent fraud, and enforce our terms of service</li>
                <li>To comply with applicable legal obligations including tax and labor law requirements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3">4. Data Sharing and Disclosure</h2>
              <p className="mb-3">We do not sell personal data. We may share information in the following circumstances:</p>

              <h3 className="font-semibold text-slate-800 mb-2">4.1 Within Your Organization</h3>
              <p className="mb-4">Employee data is accessible to authorized personnel within your organization (Company Admins and HR Managers) strictly for HR operations purposes. Employee self-service data is accessible only to the individual employee.</p>

              <h3 className="font-semibold text-slate-800 mb-2">4.2 Service Providers</h3>
              <p className="mb-4">We may share data with trusted third-party service providers (e.g., cloud hosting, email delivery) who assist in operating our platform, under strict data processing agreements that prohibit them from using data for any other purpose.</p>

              <h3 className="font-semibold text-slate-800 mb-2">4.3 Legal Requirements</h3>
              <p>We may disclose information when required by law, court order, government authority, or to protect the rights, property, or safety of our users or the public.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3">5. Data Security</h2>
              <p className="mb-3">We implement appropriate technical and organizational measures to protect personal data, including:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Encryption of data in transit using TLS/HTTPS</li>
                <li>Passwords stored using secure cryptographic hashing (bcrypt)</li>
                <li>Role-based access controls ensuring users only access data they are authorized to view</li>
                <li>JWT-based authentication with token expiry and refresh mechanisms</li>
                <li>Regular backups and audit logging</li>
              </ul>
              <p className="mt-3">
                While we strive to protect your data, no method of transmission over the internet is 100% secure. We encourage organizations to ensure their admin accounts use strong, unique passwords.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3">6. Sensitive Personal Data</h2>
              <p>
                Our platform handles sensitive personal data including Aadhaar numbers, PAN numbers, and bank account details for the purpose of payroll processing and statutory compliance. This data is collected only with the knowledge of the individual employee, stored securely, and used exclusively for the HR and payroll functions within the platform. We do not use this data for any marketing or profiling purposes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3">7. Data Retention</h2>
              <p className="mb-3">
                We retain personal data for as long as the Client's account is active and as necessary to provide services. Upon account termination:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Active employee and payroll data is retained for a minimum of 7 years to comply with Indian tax and labor regulations</li>
                <li>Clients may request data export before account closure</li>
                <li>After the retention period, data is securely deleted or anonymized</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3">8. Your Rights</h2>
              <p className="mb-3">Depending on applicable law, individuals may have the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><span className="font-medium">Access</span> — request a copy of the personal data we hold about you</li>
                <li><span className="font-medium">Correction</span> — request correction of inaccurate or incomplete data</li>
                <li><span className="font-medium">Deletion</span> — request deletion of personal data, subject to legal retention requirements</li>
                <li><span className="font-medium">Portability</span> — request your data in a portable, machine-readable format</li>
                <li><span className="font-medium">Objection</span> — object to processing of your data in certain circumstances</li>
              </ul>
              <p className="mt-3">
                Employees should contact their HR administrator to exercise these rights. Organizations can contact us directly using the details in Section 11.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3">9. Cookies</h2>
              <p>
                Teamly uses only essential session cookies required for authentication and security. We do not use tracking cookies, advertising cookies, or third-party analytics cookies. No personal data is shared with advertising networks.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3">10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. When we make material changes, we will update the "Last updated" date at the top of this page and notify active Clients via email. Continued use of the platform after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3">11. Contact Us</h2>
              <p className="mb-3">For any questions, concerns, or requests regarding this Privacy Policy or the handling of your personal data, please contact us:</p>
              <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 space-y-1">
                <p className="font-semibold text-slate-900">Spatium Offices</p>
                <p>Product: Teamly HRMS</p>
                <p>Email: <a href="mailto:support@spatiumoffices.com" className="text-primary hover:underline">support@spatiumoffices.com</a></p>
                <p>Website: <a href="https://spatiumoffices.com" className="text-primary hover:underline" target="_blank" rel="noreferrer">spatiumoffices.com</a></p>
              </div>
            </section>

          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="max-w-4xl mx-auto px-6 py-6 text-center text-sm text-slate-400">
        © {new Date().getFullYear()} Spatium Offices. All rights reserved.
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
